import { useState, useCallback, useEffect, createContext, useContext, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Check, MapPin, X } from "lucide-react";

const CATEGORIES = [
  "Bug",
  "Wrong section",
  "Missing item",
  "Needs rewording",
  "Duplicate",
  "Should not be here",
  "Other",
];

interface Pin {
  id: number;
  x: number;
  y: number;
  cat: string;
  text: string;
}

interface SuggestContextType {
  panelOpen: boolean;
  setPanelOpen: (v: boolean) => void;
  waitingForPin: boolean;
  pins: Pin[];
  pinCount: number;
}

const SuggestContext = createContext<SuggestContextType>({
  panelOpen: false,
  setPanelOpen: () => {},
  waitingForPin: false,
  pins: [],
  pinCount: 0,
});

export const useSuggest = () => useContext(SuggestContext);

export function SuggestProvider({ children }: { children: ReactNode }) {
  const [panelOpen, setPanelOpen] = useState(false);
  const [category, setCategory] = useState("");
  const [note, setNote] = useState("");
  const [waitingForPin, setWaitingForPin] = useState(false);
  const [pins, setPins] = useState<Pin[]>([]);
  const [activePin, setActivePin] = useState<number | null>(null);
  const [saved, setSaved] = useState(false);

  // Escape key cancels pin placement mode
  useEffect(() => {
    if (!waitingForPin) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setWaitingForPin(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [waitingForPin]);

  const handlePageClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (!waitingForPin) return;

      const container = e.currentTarget;
      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top + container.scrollTop;

      setPins((p) => [
        ...p,
        { id: Date.now(), x, y, cat: category, text: note },
      ]);
      setWaitingForPin(false);
      setSaved(true);
      setTimeout(() => {
        setSaved(false);
        setPanelOpen(false);
        setCategory("");
        setNote("");
      }, 1500);
    },
    [waitingForPin, category, note]
  );

  const deletePin = (id: number) => {
    setPins((p) => p.filter((pin) => pin.id !== id));
    if (activePin === id) setActivePin(null);
  };

  const handleSubmitWithoutPin = () => {
    if (!category && !note.trim()) return;
    setPins((p) => [...p, { id: Date.now(), x: -1, y: -1, cat: category, text: note }]);
    setSaved(true);
    setTimeout(() => {
      setSaved(false);
      setPanelOpen(false);
      setCategory("");
      setNote("");
    }, 1500);
  };

  const closeSuggest = () => {
    setPanelOpen(false);
    setWaitingForPin(false);
    setCategory("");
    setNote("");
    setSaved(false);
  };

  return (
    <SuggestContext.Provider
      value={{
        panelOpen,
        setPanelOpen: (v) => {
          if (v) {
            setPanelOpen(true);
            setSaved(false);
          } else {
            closeSuggest();
          }
        },
        waitingForPin,
        pins,
        pinCount: pins.length,
      }}
    >
      {/* Suggest panel — sticky below header */}
      {panelOpen && (
        <div className="border-b border-border bg-card sticky top-14 z-[45]">
          <div className="max-w-4xl mx-auto px-6 py-4">
            {saved ? (
              <div className="flex items-center justify-center gap-2 py-2">
                <Check className="w-4 h-4 text-emerald-600" />
                <span className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                  Saved! Thanks for the feedback.
                </span>
              </div>
            ) : waitingForPin ? (
              <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                <MapPin className="w-5 h-5 text-amber-700 dark:text-amber-400 shrink-0" />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-amber-800 dark:text-amber-300">
                    Now click on the page to show where the issue is
                  </div>
                  <div className="text-xs text-amber-600 dark:text-amber-500 mt-0.5">
                    Your cursor is a crosshair. Click the exact spot.
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-amber-300 text-amber-800 dark:border-amber-700 dark:text-amber-300"
                  onClick={() => setWaitingForPin(false)}
                >
                  Cancel
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-xs font-semibold text-foreground">
                    Describe what you found and click Pin to show where the issue
                    arose.
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 w-6 p-0 text-muted-foreground"
                    onClick={closeSuggest}
                  >
                    <X className="w-3.5 h-3.5" />
                  </Button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {CATEGORIES.map((c) => (
                    <button
                      key={c}
                      onClick={() => setCategory(category === c ? "" : c)}
                      className={`px-3 py-1 rounded-full text-xs font-semibold cursor-pointer transition-all border ${
                        category === c
                          ? "bg-foreground text-background border-foreground"
                          : "bg-background text-muted-foreground border-border hover:border-foreground/30"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2.5 items-start">
                  <Textarea
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Add detail (optional)..."
                    className="min-h-[44px] flex-1"
                  />
                  <div className="flex flex-col gap-1.5 shrink-0">
                    <Button
                      size="sm"
                      onClick={() => setWaitingForPin(true)}
                      variant="outline"
                      className="shrink-0"
                    >
                      <MapPin className="w-3.5 h-3.5 mr-1.5" />
                      Pin
                    </Button>
                    <Button
                      size="sm"
                      onClick={handleSubmitWithoutPin}
                      disabled={!category && !note.trim()}
                      className="shrink-0"
                    >
                      Submit
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Page content wrapper — captures pin clicks and renders pin markers */}
      <div
        onClick={handlePageClick}
        className="relative flex-1 min-h-0"
        style={{ cursor: waitingForPin ? "crosshair" : undefined }}
      >
        {/* Pin markers */}
        {pins.map((pin) => {
          const isActive = activePin === pin.id;
          const num = pins.indexOf(pin) + 1;
          return (
            <div
              key={pin.id}
              className="absolute z-40"
              style={{ left: pin.x - 13, top: pin.y - 30 }}
            >
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setActivePin(isActive ? null : pin.id);
                }}
                className="w-[26px] h-[26px] rounded-[50%_50%_50%_0] -rotate-45 flex items-center justify-center cursor-pointer shadow-lg"
                style={{
                  backgroundColor: "hsl(var(--primary))",
                  border: isActive
                    ? "2.5px solid hsl(var(--foreground))"
                    : "2.5px solid hsl(var(--background))",
                }}
              >
                <span className="rotate-45 text-[10px] font-extrabold text-primary-foreground">
                  {num}
                </span>
              </div>

              {isActive && (
                <div
                  onClick={(e) => e.stopPropagation()}
                  className="absolute top-[34px] left-0 w-[220px] bg-card rounded-lg p-3 shadow-xl border border-border z-50"
                >
                  {pin.cat && (
                    <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-accent text-accent-foreground">
                      {pin.cat}
                    </span>
                  )}
                  {pin.text && (
                    <div className="text-xs text-foreground/80 leading-relaxed mt-2">
                      {pin.text}
                    </div>
                  )}
                  {!pin.cat && !pin.text && (
                    <div className="text-xs text-muted-foreground">
                      No details added
                    </div>
                  )}
                  <button
                    onClick={() => deletePin(pin.id)}
                    className="mt-2 px-2.5 py-1 rounded-md border border-border bg-background text-[11px] font-semibold text-destructive cursor-pointer hover:bg-destructive/10 transition-colors"
                  >
                    Delete
                  </button>
                </div>
              )}
            </div>
          );
        })}

        {children}
      </div>

      {/* Bottom bar when pins exist */}
      {pins.length > 0 && !panelOpen && (
        <div className="fixed bottom-0 left-0 right-0 px-6 py-3 bg-card border-t border-border flex justify-center z-50">
          <div className="max-w-4xl w-full flex items-center gap-3">
            <div className="flex-1 text-sm text-muted-foreground">
              <span className="font-bold text-foreground">{pins.length}</span>{" "}
              suggestion{pins.length !== 1 ? "s" : ""} pinned
            </div>
            <Button size="sm">Submit all</Button>
          </div>
        </div>
      )}
    </SuggestContext.Provider>
  );
}
